import { type RxCollection, type RxDatabase } from 'rxdb';
import { BehaviorSubject, firstValueFrom, Observable } from 'rxjs';
import { filter, map, timeout } from 'rxjs/operators';
import {
  acquireTabPresenceLock,
  addAirshipCollections,
  createDatabase,
  onTabHidden,
  queryLocks,
  removeDatabaseAndKeys,
  TAB_PRESENCE_LOCK_NAME,
  waitForTabVisible,
  wipeIfSchemaChanged,
  withInitLock,
} from './rxDbInstance';
import {
  type AirshipCollections,
  type CollectionName,
  type DocOf,
  type RxDbActionRunner,
} from './types';

const INIT_LOCK_TIMEOUT_MS = 6_000;

export const DB_INIT_TIMEOUT_MS = 40_000;
export const RECONNECT_DELAY_MS = 1_000;
export const RELOAD_TIMEOUT_MS = 5_000;

export type UnavailableReason =
  | { kind: 'init-timeout' }
  | { kind: 'reload-blocked' }
  | { kind: 'disposed' }
  | { kind: 'unknown'; detail: unknown };

export type ResetSource = 'error-screen' | 'diagnostics';
export type RxDbPhase =
  | { status: 'closed' }
  | { status: 'awaiting-visibility' }
  | { status: 'opening' }
  | { status: 'active' }
  | { status: 'resetting'; trigger: 'connection-lost' }
  | {
      status: 'resetting';
      trigger: 'manual-destructive';
      source: ResetSource;
    }
  | { status: 'unavailable'; reason: UnavailableReason };

export type TabPresence = {
  otherTabCount: number;
};

export type SchemaRebuildProblem = {
  reason: 'failed';
  detail: unknown;
};

export type RxDbSnapshot = {
  phase: RxDbPhase;
  leadership: 'leader' | 'follower';
  tabPresence: TabPresence | null;
  pendingWriteCount: number | null;
  schemaRebuildProblem: SchemaRebuildProblem | null;
};

const initialSnapshot: RxDbSnapshot = {
  phase: { status: 'closed' },
  leadership: 'follower',
  tabPresence: null,
  pendingWriteCount: null,
  schemaRebuildProblem: null,
};

export type RxDbConnectionServiceDeps = {
  createDatabase: () => Promise<RxDatabase<AirshipCollections>>;
  addCollections: (db: RxDatabase<AirshipCollections>) => Promise<void>;
  removeDatabaseAndKeys: () => Promise<void>;
  wipeIfSchemaChanged: () => Promise<void>;
  useTimeout: (timeMs: number) => Promise<void>;
  location: Pick<Location, 'reload'>;
  locks: {
    waitForTabVisible: () => Promise<void>;
    onTabHidden: (cb: () => void) => () => void;
    acquireTabPresenceLock: () => Promise<void>;
    queryLocks: () => Promise<LockManagerSnapshot | null>;
    withInitLock: <T>(
      options: { lockTimeout: number },
      fn: () => Promise<T>
    ) => Promise<T>;
  };
};

export class RxDbUnavailableError extends Error {
  constructor(public readonly reason: UnavailableReason) {
    super(`RxDB unavailable: ${reason.kind}`);
    this.name = 'RxDbUnavailableError';
  }
}

export class RxDbConnectionService implements RxDbActionRunner {
  readonly status$: Observable<RxDbSnapshot>;

  private readonly statusSubject = new BehaviorSubject<RxDbSnapshot>(
    initialSnapshot
  );
  private dbHandle: RxDatabase<AirshipCollections> | null = null;
  private startPromise: Promise<void> | null = null;
  private presenceLockRequested = false;
  private generation = 0;

  constructor(private readonly deps: RxDbConnectionServiceDeps) {
    this.status$ = this.statusSubject.asObservable();
  }

  start(): void {
    if (this.startPromise) {
      return;
    }
    this.acquireTabPresence();
    const generation = ++this.generation;
    this.startPromise = this.runOpenSequence(generation);
  }

  dispose(): void {
    this.generation++;
    this.startPromise = null;
    this.dbHandle = null;
    this.setSnapshot({
      phase: { status: 'unavailable', reason: { kind: 'disposed' } },
    });
  }

  async reset(source: ResetSource): Promise<void> {
    const generation = ++this.generation;
    this.startPromise = null;
    this.setSnapshot({
      phase: { status: 'resetting', trigger: 'manual-destructive', source },
    });

    try {
      await this.destroyStorage();
    } catch {
      // best-effort — the reload is what recovers the app, not a clean wipe
    } finally {
      this.dbHandle = null;
      this.deps.location.reload();
      this.armReloadWatchdog(generation);
    }
  }

  async wipeForLogout(): Promise<void> {
    this.generation++;
    try {
      await this.destroyStorage();
    } catch {
      // best-effort
    } finally {
      this.dbHandle = null;
      this.dispose();
    }
  }

  async refreshTabPresence(): Promise<void> {
    const state = await this.deps.locks.queryLocks();
    if (!state) {
      this.setSnapshot({ tabPresence: null });
      return;
    }
    const held = state.held ?? [];
    const presence: TabPresence = {
      otherTabCount: Math.max(
        0,
        held.filter((l) => l.name === TAB_PRESENCE_LOCK_NAME).length - 1
      ),
    };
    this.setSnapshot({ tabPresence: presence });
  }

  async doAction<K extends CollectionName, T>(
    collection: K,
    action: (col: RxCollection<DocOf<K>>) => Promise<T>
  ): Promise<T> {
    const db = await this.whenActive();
    return action(this.collectionOf(db, collection));
  }

  private setSnapshot(patch: Partial<RxDbSnapshot>): void {
    this.statusSubject.next({ ...this.statusSubject.getValue(), ...patch });
  }

  private async destroyStorage(): Promise<void> {
    if (this.dbHandle) {
      await this.dbHandle.remove().catch(() => undefined);
    }
    await this.deps.removeDatabaseAndKeys();
  }

  private async runOpenSequence(generation: number): Promise<void> {
    const isCurrent = () => generation === this.generation;

    this.setSnapshot({ phase: { status: 'awaiting-visibility' } });
    await this.deps.locks.waitForTabVisible();
    if (!isCurrent()) {
      return;
    }

    this.setSnapshot({ phase: { status: 'opening' } });

    this.armInitWatchdog(isCurrent);

    try {
      await this.deps.locks.withInitLock(
        { lockTimeout: INIT_LOCK_TIMEOUT_MS },
        async () => {
          await this.deps.wipeIfSchemaChanged().catch((detail) => {
            if (isCurrent()) {
              this.setSnapshot({
                schemaRebuildProblem: { reason: 'failed', detail },
              });
            }
          });

          const db = await this.deps.createDatabase();
          if (!isCurrent()) {
            return;
          }
          this.dbHandle = db;
          await this.deps.addCollections(db);
        }
      );
      if (!isCurrent()) {
        return;
      }
      this.becomeActive(generation);
    } catch (err) {
      if (!isCurrent()) {
        return;
      }
      this.setSnapshot({
        phase: {
          status: 'unavailable',
          reason: { kind: 'unknown', detail: err },
        },
      });
    }
  }

  private armReloadWatchdog(generation: number): void {
    void this.deps.useTimeout(RELOAD_TIMEOUT_MS).then(() => {
      if (generation !== this.generation) {
        return;
      }
      this.setSnapshot({
        phase: { status: 'unavailable', reason: { kind: 'reload-blocked' } },
      });
    });
  }

  private armInitWatchdog(isCurrent: () => boolean): void {
    let sawHidden = false;
    const unsubscribe = this.deps.locks.onTabHidden(() => {
      sawHidden = true;
    });

    const run = async (): Promise<void> => {
      await this.deps.useTimeout(DB_INIT_TIMEOUT_MS);

      const stillOpening =
        isCurrent() &&
        ['awaiting-visibility', 'opening'].includes(
          this.statusSubject.getValue().phase.status
        );
      if (!stillOpening) {
        unsubscribe();
        return;
      }

      if (sawHidden) {
        sawHidden = false;
        await this.deps.locks.waitForTabVisible();
        return run();
      }

      unsubscribe();
      this.setSnapshot({
        phase: { status: 'unavailable', reason: { kind: 'init-timeout' } },
      });
    };

    void run();
  }

  private becomeActive(generation: number): void {
    const db = this.dbHandle;
    const isLeaderNow = !!db && db.isLeader();
    this.setSnapshot({
      phase: { status: 'active' },
      leadership: isLeaderNow ? 'leader' : 'follower',
    });
    if (!isLeaderNow) {
      this.watchForLeadershipChange(generation);
    }
    this.registerConnectionLossHandler(generation);
  }

  private registerConnectionLossHandler(generation: number): void {
    const db = this.dbHandle;
    if (!db) {
      return;
    }
    db.onClose.push(() => {
      if (generation !== this.generation) {
        return;
      }
      const newGeneration = ++this.generation;
      this.dbHandle = null;
      this.setSnapshot({
        phase: { status: 'resetting', trigger: 'connection-lost' },
      });
      this.startPromise = this.reconnectAfterDelay(newGeneration);
    });
  }

  private async reconnectAfterDelay(generation: number): Promise<void> {
    await this.deps.useTimeout(RECONNECT_DELAY_MS);
    if (generation !== this.generation) {
      return;
    }
    return this.runOpenSequence(generation);
  }

  private watchForLeadershipChange(generation: number): void {
    const db = this.dbHandle;
    if (!db || typeof db.waitForLeadership !== 'function') {
      return;
    }
    db.waitForLeadership()
      .then((isLeader) => {
        if (isLeader && generation === this.generation) {
          this.setSnapshot({ leadership: 'leader' });
        }
      })
      .catch(() => void 0);
  }

  private acquireTabPresence(): void {
    if (this.presenceLockRequested) {
      return;
    }
    this.presenceLockRequested = true;
    this.deps.locks.acquireTabPresenceLock().catch(() => {
      this.presenceLockRequested = false;
    });
  }

  private async whenActive(): Promise<RxDatabase<AirshipCollections>> {
    const current = this.statusSubject.getValue();
    if (current.phase.status === 'active' && this.dbHandle) {
      return this.dbHandle;
    }
    if (current.phase.status === 'unavailable') {
      throw new RxDbUnavailableError(current.phase.reason);
    }

    try {
      return await firstValueFrom(
        this.status$.pipe(
          filter(
            (s) =>
              s.phase.status === 'active' || s.phase.status === 'unavailable'
          ),
          map((s) => {
            if (s.phase.status === 'unavailable') {
              throw new RxDbUnavailableError(s.phase.reason);
            }
            if (!this.dbHandle) {
              throw new RxDbUnavailableError({
                kind: 'unknown',
                detail: 'db handle missing after active phase',
              });
            }
            return this.dbHandle;
          }),
          timeout(DB_INIT_TIMEOUT_MS)
        )
      );
    } catch (err) {
      if (err instanceof RxDbUnavailableError) {
        throw err;
      }
      throw new RxDbUnavailableError({ kind: 'init-timeout' });
    }
  }

  private collectionOf<K extends CollectionName>(
    db: RxDatabase<AirshipCollections>,
    collection: K
  ): RxCollection<DocOf<K>> {
    return db[collection] as unknown as RxCollection<DocOf<K>>;
  }
}

const useTimeout = (timeMs: number) =>
  new Promise<void>((res) => setTimeout(res, timeMs));

export const createRxDbConnectionService = ({
  locks,
  ...overrides
}: Partial<RxDbConnectionServiceDeps> = {}): RxDbConnectionService =>
  new RxDbConnectionService({
    locks: {
      waitForTabVisible,
      onTabHidden,
      withInitLock,
      acquireTabPresenceLock,
      queryLocks,
      ...locks,
    },
    createDatabase,
    addCollections: addAirshipCollections,
    removeDatabaseAndKeys,
    wipeIfSchemaChanged,
    useTimeout,
    location: window.location,
    ...overrides,
  });
